import { REQUEST_STATUS } from "lib/types";
import { createReducer, reduce } from "re-reduced";
import { PersistConfig, persistReducer } from "redux-persist";
import storage from "redux-persist/lib/storage/index.js";

import actions from "./actions";
import { CoreState, UsageType } from "./types";

const INITIAL_STATE: CoreState = {
  validateAddressRequestStatus: REQUEST_STATUS.Idle,
  verificationRequestStatus: REQUEST_STATUS.Idle,
  verifyCodeRequestStatus: REQUEST_STATUS.Idle,
  createFlickSignUpRequestStatus: REQUEST_STATUS.Idle,
  updateFlickSignUpRequestStatus: REQUEST_STATUS.Idle,
  getFlickSignUpRequestStatus: REQUEST_STATUS.Idle,
  setCampaignLitresStatus: REQUEST_STATUS.Idle,
  completeRegistrationRequestStatus: REQUEST_STATUS.Idle,
  windcaveTransactionRequestStatus: REQUEST_STATUS.Idle,
  windcaveAuthorisationRequestStatus: REQUEST_STATUS.Idle,
  rateCardsRequestStatus: REQUEST_STATUS.Idle,
  eligibleAddresses: {
    data: [],
    included: [],
  },
  userId: null,
  flickUserId: null,
  user: {
    checks: {
      marketingAccepted: false,
      smsSwitchNotificationsAccepted: false,
      termsAccepted: false,
    },
    details: {
      dob: null,
      emailAddress: "",
      firstName: "",
      lastName: "",
      mobileNumber: "",
      prefName: "",
    },
    rewards: {
      fuelType: undefined,
    },
    payment: {
      paymentType: "credit_card",
      creditCheckAccecpted: false,
    },
    property: {
      usageType: UsageType.STANDARD,
      medicallyDependent: undefined,
      dwellingType: undefined,
      situation: undefined,
    },
  },
  campaigns: [],
  signUpState: "0",
  currentFormStep: "/",
  partialSignUpDialogOpen: false,
  promoCode: undefined,
  leadSource: undefined,
  selectedProduct: undefined,
  productMetadata: {
    includeEVcharger: false,
    evChargerColor: undefined,
  },
};

const persistConfig: PersistConfig<CoreState> = {
  storage,
  key: `@z-electric:core`,
  version: 2,
  blacklist: [
    "siteRequestStatus",
    "validateAddressRequestStatus",
    "verificationRequestStatus",
    "createFlickSignUpRequestStatus",
    "updateFlickSignUpRequestStatus",
    "getFlickSignUpRequestStatus",
    "verifyCodeRequestStatus",
    "windcaveTransactionRequestStatus",
    "windcaveAuthorisationRequestStatus",
    "completeRegistrationRequestStatus",
    "windcaveSignUpId",
  ],
};

const reducer = createReducer<CoreState>(
  [
    // User
    reduce(actions.setUserDetails, (state, payload) => ({
      ...state,
      user: {
        ...state.user,
        details: payload,
      },
    })),
    reduce(actions.setUserRewards, (state, payload) => ({
      ...state,
      user: {
        ...state.user,
        rewards: payload,
      },
    })),
    reduce(actions.setUserProperty, (state, payload) => ({
      ...state,
      user: {
        ...state.user,
        property: {
          ...state.user.property,
          ...payload,
        },
      },
    })),
    reduce(actions.setUsageType, (state, payload) => ({
      ...state,
      user: {
        ...state.user,
        property: {
          ...state.user.property,
          usageType: payload,
        },
      },
    })),
    reduce(actions.setUserChecks, (state, payload) => ({
      ...state,
      user: {
        ...state.user,
        checks: payload,
      },
    })),
    reduce(actions.setUserPayment, (state, payload) => ({
      ...state,
      user: {
        ...state.user,
        payment: payload,
      },
    })),
    reduce(actions.setCampaigns, (state, payload) => ({
      ...state,
      campaigns: payload,
    })),

    reduce(actions.setCurrentFormStep, (state, payload) => ({
      ...state,
      currentFormStep: payload,
    })),

    reduce(actions.setPartialSignUpDialogState, (state, payload) => ({
      ...state,
      partialSignUpDialogOpen: payload,
    })),

    reduce(actions.setPromoCode, (state, payload) => ({
      ...state,
      promoCode: payload,
      flickAccountError: undefined,
    })),

    reduce(actions.setLeadSource, (state, payload) => ({
      ...state,
      leadSource: payload,
    })),

    reduce(actions.setSelectedProduct, (state, payload) => ({
      ...state,
      selectedProduct: payload,
    })),

    reduce(actions.setEvChargerInclusion, (state, payload) => ({
      ...state,
      productMetadata: {
        ...state.productMetadata,
        includeEVcharger: payload,
      },
    })),

    reduce(actions.setEvChargerColor, (state, payload) => ({
      ...state,
      productMetadata: {
        ...state.productMetadata,
        evChargerColor: payload,
      },
    })),

    // ---------------------Address-------------------------------
    // -----------------------------------------------------------

    reduce(
      [actions.validateAddress.request, actions.validateICP.request],
      (state) => ({
        ...state,
        validateAddressRequestStatus: REQUEST_STATUS.Pending,
        selectedAddress: undefined,
      }),
    ),
    reduce(
      [actions.validateAddress.failure, actions.validateICP.failure],
      (state) => ({
        ...state,
        validateAddressRequestStatus: REQUEST_STATUS.Failed,
      }),
    ),
    reduce(
      [actions.validateAddress.success, actions.validateICP.success],
      (state, payload) => ({
        ...state,
        validateAddressRequestStatus: REQUEST_STATUS.Fulfilled,
        eligibleAddresses: payload.eligibleAddresses,
      }),
    ),
    reduce(actions.selectAddress, (state, payload) => ({
      ...state,
      validateAddressRequestStatus: REQUEST_STATUS.Fulfilled,
      selectedAddress: payload.address,
      selectedEligibilityType: payload.eligibility,
    })),
    reduce(actions.updateEligibility, (state, payload) => ({
      ...state,
      selectedEligibilityType: payload.eligibility,
      rateCardsRequestStatus: REQUEST_STATUS.Fulfilled,
    })),
    reduce(actions.fetchRateCards.request, (state) => ({
      ...state,
      rateCardsRequestStatus: REQUEST_STATUS.Pending,
    })),
    reduce(actions.fetchRateCards.failure, (state) => ({
      ...state,
      rateCardsRequestStatus: REQUEST_STATUS.Failed,
    })),
    reduce(actions.fetchRateCards.success, (state, payload) => ({
      ...state,
      rateCardsRequestStatus: REQUEST_STATUS.Fulfilled,
      rateCards: payload,
    })),

    // -----------------------Mobile------------------------------
    // -----------------------------------------------------------

    reduce(actions.verifyMobile.request, (state) => ({
      ...state,
      verificationRequestStatus: REQUEST_STATUS.Pending,
    })),
    reduce(actions.verifyMobile.failure, (state) => ({
      ...state,
      verificationRequestStatus: REQUEST_STATUS.Failed,
    })),
    reduce(actions.verifyMobile.success, (state, payload) => ({
      ...state,
      verificationRequestStatus: REQUEST_STATUS.Fulfilled,
      verifyId: payload.id,
    })),
    reduce(actions.verifyCode.request, (state) => ({
      ...state,
      verifyCodeRequestStatus: REQUEST_STATUS.Pending,
    })),
    reduce(actions.verifyCode.failure, (state) => ({
      ...state,
      verifyCodeRequestStatus: REQUEST_STATUS.Failed,
    })),
    reduce(actions.verifyCode.success, (state, payload) => ({
      ...state,
      verifyCodeRequestStatus: REQUEST_STATUS.Fulfilled,
      userId: payload.userId,
    })),

    // ------------------------Payment----------------------------
    // -----------------------------------------------------------

    reduce(actions.createWindcaveTransaction.request, (state) => ({
      ...state,
      windcaveTransactionRequestStatus: REQUEST_STATUS.Pending,
    })),
    reduce(actions.createWindcaveTransaction.failure, (state) => ({
      ...state,
      windcaveTransactionRequestStatus: REQUEST_STATUS.Failed,
    })),
    reduce(actions.createWindcaveTransaction.success, (state, payload) => ({
      ...state,
      windcaveSignUpId: payload.data.attributes.sign_up_id,
      transactionId: payload.data.id,
      windcaveTransactionRequestStatus: REQUEST_STATUS.Fulfilled,
    })),
    reduce(actions.checkSuccessfulAuthorisation.request, (state) => ({
      ...state,
      windcaveAuthorisationRequestStatus: REQUEST_STATUS.Pending,
    })),
    reduce(actions.checkSuccessfulAuthorisation.failure, (state) => ({
      ...state,
      windcaveAuthorisationRequestStatus: REQUEST_STATUS.Failed,
    })),
    reduce(actions.checkSuccessfulAuthorisation.success, (state, payload) => ({
      ...state,
      windcaveSignUpId: payload.data.attributes.sign_up_id,
      windcaveAuthorisationRequestStatus: REQUEST_STATUS.Fulfilled,
    })),

    // -------------------------Account---------------------------
    // -----------------------------------------------------------

    reduce(actions.createFlickSignUp.request, (state) => ({
      ...state,
      flickUserId: null,
      createFlickSignUpRequestStatus: REQUEST_STATUS.Pending,
    })),
    reduce(actions.createFlickSignUp.failure, (state, payload) => ({
      ...state,
      createFlickSignUpRequestStatus: REQUEST_STATUS.Failed,
      flickAccountError: payload,
    })),
    reduce(actions.createFlickSignUp.success, (state, payload) => ({
      ...state,
      createFlickSignUpRequestStatus: REQUEST_STATUS.Fulfilled,
      flickUserId: payload.data.id,
    })),
    reduce(actions.updateFlickSignUp.request, (state) => ({
      ...state,
      updateFlickSignUpRequestStatus: REQUEST_STATUS.Pending,
      flickAccountError: undefined,
    })),
    reduce(actions.updateFlickSignUp.failure, (state, payload) => ({
      ...state,
      updateFlickSignUpRequestStatus: REQUEST_STATUS.Failed,
      flickAccountError: payload,
    })),
    reduce(actions.updateFlickSignUp.success, (state) => ({
      ...state,
      updateFlickSignUpRequestStatus: REQUEST_STATUS.Fulfilled,
    })),
    reduce(actions.setFlickUserId, (state, payload) => ({
      ...state,
      flickUserId: payload,
    })),
    reduce(actions.getFlickSignUp.request, (state) => ({
      ...state,
      getFlickSignUpRequestStatus: REQUEST_STATUS.Pending,
    })),
    reduce(actions.getFlickSignUp.failure, (state, payload) => ({
      ...state,
      getFlickSignUpRequestStatus: REQUEST_STATUS.Failed,
      flickAccountError: payload,
    })),
    reduce(actions.getFlickSignUp.success, (state) => ({
      ...state,
      getFlickSignUpRequestStatus: REQUEST_STATUS.Fulfilled,
    })),
    reduce(actions.setFlickSignUpStateComplete, (state) => ({
      ...state,
      signUpState: "complete",
    })),
    reduce(actions.completeRegistration.request, (state) => ({
      ...state,
      completeRegistrationRequestStatus: REQUEST_STATUS.Pending,
    })),
    reduce(actions.completeRegistration.failure, (state) => ({
      ...state,
      completeRegistrationRequestStatus: REQUEST_STATUS.Failed,
    })),
    reduce(actions.completeRegistration.success, (state) => ({
      ...state,
      completeRegistrationRequestStatus: REQUEST_STATUS.Fulfilled,
    })),
    reduce(actions.setCreationSourceIdentifier, (state) => ({
      ...state,
      creationSource: "sign_up_api_guided",
    })),

    // ----------------------Abandoned Cart-----------------------
    // -----------------------------------------------------------
    reduce(actions.setAddress, (state, payload) => ({
      ...state,
      selectedAddress: payload,
    })),
    reduce(actions.setCampaignLitres.request, (state) => ({
      ...state,
      setCampaignLitresStatus: REQUEST_STATUS.Pending,
    })),
    reduce(actions.setCampaignLitres.failure, (state) => ({
      ...state,
      setCampaignLitresStatus: REQUEST_STATUS.Failed,
    })),
    reduce(actions.setCampaignLitres.success, (state) => ({
      ...state,
      setCampaignLitresStatus: REQUEST_STATUS.Fulfilled,
    })),
  ],
  INITIAL_STATE,
);

export default persistReducer(persistConfig, reducer);
